import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { generateGlossaryFromText } from '../../../services/aiGlossaryGenerationService';

const AIGlossaryGenerator = ({ plays, onTermsGenerated }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shakespeareText, setShakespeareText] = useState('');
  const [selectedPlayId, setSelectedPlayId] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generatedCount, setGeneratedCount] = useState(0);

  const handleGenerate = async () => {
    if (!shakespeareText?.trim()) {
      setError('Please enter Shakespeare text to analyze');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);
      setGeneratedCount(0);

      const selectedPlay = plays?.find(p => p?.id === selectedPlayId);
      const playTitle = selectedPlay?.title || '';

      const generatedTerms = await generateGlossaryFromText(shakespeareText, playTitle);

      if (generatedTerms?.length === 0) {
        setError('No archaic words found in the provided text');
        return;
      }

      // Add playId to each term if a play is selected
      const termsWithPlay = generatedTerms?.map(term => ({
        ...term,
        playId: selectedPlayId || null
      }));

      setGeneratedCount(termsWithPlay?.length);
      onTermsGenerated?.(termsWithPlay);

      // Clear the text area after successful generation
      setShakespeareText('');
    } catch (err) {
      console.error('Error generating glossary:', err);
      setError(err?.message || 'Failed to generate glossary terms');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClear = () => {
    setShakespeareText('');
    setSelectedPlayId('');
    setError(null);
    setGeneratedCount(0);
  };

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg border-2 border-purple-200 dark:border-purple-800 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/50 dark:hover:bg-black/20 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-purple-500 to-blue-500 p-2 rounded-lg">
            <Icon name="Sparkles" size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              AI Glossary Generator
              <span className="text-xs font-normal bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 px-2 py-0.5 rounded-full">
                Powered by OpenAI
              </span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Auto-generate archaic word definitions and pronunciation guides from Shakespeare text
            </p>
          </div>
        </div>
        <Icon
          name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
          size={20}
          className="text-muted-foreground"
        />
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-purple-200 dark:border-purple-800">
          {/* Success Message */}
          {generatedCount > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-start gap-2">
              <Icon name="CheckCircle2" size={18} className="text-green-600 dark:text-green-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  Successfully generated {generatedCount} glossary term{generatedCount !== 1 ? 's' : ''}!
                </p>
                <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                  Review and save the terms below to add them to your glossary.
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="text-red-600 dark:text-red-400 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-300">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {/* Play Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Play (Optional)
            </label>
            <select
              value={selectedPlayId}
              onChange={(e) => setSelectedPlayId(e?.target?.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-foreground"
              disabled={isGenerating}
            >
              <option value="">No specific play</option>
              {plays?.map((play) => (
                <option key={play?.id} value={play?.id}>
                  {play?.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              Selecting a play helps provide better context for definitions
            </p>
          </div>

          {/* Text Input */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Shakespeare Text
            </label>
            <textarea
              value={shakespeareText}
              onChange={(e) => setShakespeareText(e?.target?.value)}
              placeholder="Paste Shakespeare text here... (e.g., a scene, monologue, or dialogue)&#10;&#10;Example:&#10;To be, or not to be, that is the question:&#10;Whether 'tis nobler in the mind to suffer&#10;The slings and arrows of outrageous fortune..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-foreground font-mono text-sm min-h-[200px] resize-y"
              disabled={isGenerating}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-muted-foreground">
                {shakespeareText?.length} characters
              </p>
              <p className="text-xs text-muted-foreground">
                Recommended: 100-2000 characters for best results
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <Button
              onClick={handleGenerate}
              disabled={isGenerating || !shakespeareText?.trim()}
              className="flex-1 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
            >
              {isGenerating ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Icon name="Sparkles" size={16} />
                  Generate Glossary
                </>
              )}
            </Button>
            <Button
              onClick={handleClear}
              variant="outline"
              disabled={isGenerating}
            >
              <Icon name="X" size={16} />
              Clear
            </Button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
                <p className="font-medium">How it works:</p>
                <ul className="list-disc list-inside space-y-0.5 ml-2">
                  <li>AI analyzes your Shakespeare text for archaic words</li>
                  <li>Generates clear definitions and IPA pronunciation guides</li>
                  <li>Assigns difficulty levels (easy, medium, hard)</li>
                  <li>Review generated terms before adding to glossary</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIGlossaryGenerator;
